import Image from 'next/image';
import React, { useState, useEffect, useMemo } from 'react';

import { AlertPopup } from '@/components/alert-popup';
import { useAppContext } from '@/hooks/use-app-context';

/**
 * 空間履歴スライダーコンポーネントのプロパティ
 */
export interface HistorySliderProps {
  /** サムネイルを表示するかどうか */
  showThumbnails?: boolean;
  /** スライダーを無効化するかどうか */
  disabled?: boolean;
  /** 過去方向への移動かどうかを設定する関数 */
  setIsMovingToPast?: (isPast: boolean) => void;
}

/**
 * 空間履歴スライダーコンポーネント
 *
 * 画面上部に表示される空間履歴スライダー。過去の空間情報に変更できる。
 */
export function HistorySlider({ showThumbnails = false, disabled = false, setIsMovingToPast }: HistorySliderProps) {
  // アプリコンテキストから情報を取得
  const { state, dispatch, switchHistory, getCurrentRoom } = useAppContext();
  const { currentHistoryId, isRoomListVisible } = state;

  // アラートポップアップの状態
  const [isAlertVisible, setIsAlertVisible] = useState(false);

  // 現在のルームの空間履歴を取得
  const currentRoom = getCurrentRoom();
  // useMemoを使用してhistoriesの初期化をメモ化し、日付順にソート
  const histories = useMemo(() => {
    const roomHistories = currentRoom?.histories || [];

    // 日付順にソート（新しい日付が配列の後ろに来るように）
    return [...roomHistories].sort((a, b) => {
      // 日付が空の場合は最も古い日付として扱う
      if (!a.date) return -1;
      if (!b.date) return 1;

      // 日付文字列をDateオブジェクトに変換して比較
      const dateA = new Date(a.date.replace(/\//g, '-'));
      const dateB = new Date(b.date.replace(/\//g, '-'));

      return dateA.getTime() - dateB.getTime();
    });
  }, [currentRoom]);

  // 現在選択されている履歴のインデックス
  // 初期値は0に設定し、useEffectで最新の履歴に更新
  const [selectedIndex, setSelectedIndex] = useState(0);

  // 現在の履歴IDが変更されたらインデックスを更新
  useEffect(() => {
    const index = histories.findIndex((history) => history.id === currentHistoryId);
    if (index !== -1) {
      setSelectedIndex(index);
    } else if (histories.length > 0) {
      // currentHistoryIdに対応する履歴が見つからない場合は最新の履歴を選択
      setSelectedIndex(histories.length - 1);
    }
  }, [currentHistoryId, histories]);

  // スライダー変更時のハンドラー
  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) {
      // ゲストモードで同期有効時はアラートポップアップを表示
      setIsAlertVisible(true);
      return;
    }

    const newIndex = parseInt(event.target.value);
    const oldIndex = selectedIndex;

    // 過去方向への移動かどうかを判断（インデックスが小さくなる場合は過去方向）
    const isPast = newIndex < oldIndex;
    if (setIsMovingToPast) {
      setIsMovingToPast(isPast);
    }

    setSelectedIndex(newIndex);
    if (histories[newIndex]) {
      // 履歴切り替え前にisHistorySliderChangeをtrueに設定
      dispatch({ type: 'SET_HISTORY_SLIDER_CHANGE', payload: true });
      console.log('[HISTORY_SLIDER] isHistorySliderChangeをtrueに設定しました');
      console.log(`[HISTORY_SLIDER] 移動方向: ${isPast ? '過去' : '未来'}`);
      switchHistory(histories[newIndex].id);
    }
  };

  // 履歴ポイントをクリックしたときのハンドラー
  const handlePointClick = (index: number) => {
    if (disabled) {
      // ゲストモードで同期有効時はアラートポップアップを表示
      setIsAlertVisible(true);
      return;
    }

    // 過去方向への移動かどうかを判断（インデックスが小さくなる場合は過去方向）
    const isPast = index < selectedIndex;
    if (setIsMovingToPast) {
      setIsMovingToPast(isPast);
    }

    setSelectedIndex(index);
    if (histories[index]) {
      // 履歴切り替え前にisHistorySliderChangeをtrueに設定
      dispatch({ type: 'SET_HISTORY_SLIDER_CHANGE', payload: true });
      console.log('[HISTORY_SLIDER] isHistorySliderChangeをtrueに設定しました');
      console.log(`[HISTORY_SLIDER] 移動方向: ${isPast ? '過去' : '未来'}`);
      switchHistory(histories[index].id);
    }
  };

  // アラートポップアップが閉じられたときのハンドラー
  const handleAlertClose = () => {
    setIsAlertVisible(false);
  };

  // スライダーの位置に基づいて、マーカーの位置を計算
  const calculateMarkerPosition = (index: number, total: number) => {
    if (total <= 1) return '0%';

    // 両端から24pxの位置を計算
    const insetPx = 24;

    // 最初のマーカー（index=0）は左端から24px内側に
    if (index === 0) return `${insetPx}px`;

    // 最後のマーカー（index=total-1）は右端から24px内側に
    if (index === total - 1) return `calc(100% - ${insetPx}px)`;

    // それ以外のマーカーは、両端の間で均等に分布
    // 注：この計算は近似値になります
    return `calc(${insetPx}px + (100% - ${insetPx * 2}px) * ${index} / ${total - 1})`;
  };

  // 固定スタイル設定
  const sliderTop = '88px';
  // 左右の余白を368pxに設定し、最大幅を703pxに制限
  const horizontalMargin = 368; // px単位
  const sliderWidth = '703px';
  const sliderPadding = '10px';
  const sliderHeight = '60px';

  const markerSize = '16px';
  const markerTop = '-6px';

  const yearFontSize = '14px';
  const dateFontSize = '14px';
  const dateMarginTop = '8px';

  // 履歴が1つ以下の場合、またはルームリストが表示されている場合は表示しない
  if (histories.length <= 1 || isRoomListVisible) {
    return null;
  }

  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: sliderTop,
          left: '50%',
          transform: 'translateX(-50%)',
          width: sliderWidth,
          maxWidth: `calc(100% - ${horizontalMargin * 2}px)`,
          padding: sliderPadding,
          zIndex: 1000
        }}
      >
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: sliderHeight,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          {/* スライダー */}
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: '6px',
              background: `linear-gradient(to right,
              #22A9BC 100%,
              #22A9BC ${calculateMarkerPosition(selectedIndex, histories.length)},
              rgba(200, 200, 200, 0.8) ${calculateMarkerPosition(selectedIndex, histories.length)},
              rgba(200, 200, 200, 0.8) 100%)`,
              borderRadius: '3px',
              marginTop: '20px',
              boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.3)'
            }}
          >
            <input
              className={disabled ? 'cursor-not-allowed' : ''}
              max={histories.length - 1}
              min={0}
              type='range'
              value={selectedIndex}
              style={{
                position: 'absolute',
                top: '-8px',
                width: '100%',
                height: '20px',
                opacity: 0,
                cursor: disabled ? 'not-allowed' : 'pointer' // 互換性のために残す
              }}
              onChange={handleSliderChange}
            />

            {/* スライダーのマーカー */}
            <div
              style={{
                position: 'absolute',
                top: markerTop,
                left: calculateMarkerPosition(selectedIndex, histories.length),
                width: markerSize,
                height: markerSize,
                backgroundColor: '#22A9BC',
                borderRadius: '50%',
                transform: 'translateX(-50%)',
                boxShadow: '0 0 0 4px white, 0 2px 8px rgba(0, 0, 0, 0.3)',
                zIndex: 10
              }}
            />

            {/* 日付マーカー */}
            {histories.map((history, index) => {
              // 日付から年と月/日を抽出
              const [year, month, day] = history.date.split('/');
              const displayDate = month && day ? `${month}/${day}` : '';

              // 前の年と同じ場合は年を表示しない
              const showYear =
                index === 0 ||
                (index > 0 && histories[index]?.date.split('/')[0] !== histories[index - 1]?.date.split('/')[0]);

              return (
                <div
                  key={index}
                  className={disabled ? 'cursor-not-allowed' : ''}
                  style={{
                    position: 'absolute',
                    top: '0',
                    left: calculateMarkerPosition(index, histories.length),
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: disabled ? 'not-allowed' : 'pointer', // 互換性のために残す
                    zIndex: 5
                  }}
                  onClick={() => handlePointClick(index)}
                >
                  {/* ノーマルポイント */}
                  <div
                    style={{
                      width: markerSize,
                      height: markerSize,
                      backgroundColor: index === selectedIndex ? '#22A9BC' : '#22A9BC',
                      borderRadius: '50%',
                      marginTop: '-4px',
                      boxShadow: '0 0 0 2px rgba(0, 0, 0, 0.3)'
                    }}
                  />
                  <div
                    style={{
                      color: 'white',
                      fontSize: yearFontSize,
                      fontWeight: 'bold',
                      marginTop: dateMarginTop,
                      textAlign: 'center',
                      visibility: showYear ? 'visible' : 'hidden',
                      height: '18px', // 高さを固定して空白を維持
                      textShadow: getTextShadow()
                    }}
                  >
                    {year}
                  </div>
                  <div
                    style={{
                      color: 'white',
                      fontSize: dateFontSize,
                      fontWeight: 'bold',
                      marginTop: '2px',
                      textAlign: 'center',
                      textShadow: getTextShadow()
                    }}
                  >
                    {displayDate}
                  </div>

                  {/* サムネイル */}
                  {showThumbnails && history.thumbnailUrl && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: index === selectedIndex ? 'block' : 'none',
                        marginBottom: '10px'
                      }}
                    >
                      <Image
                        alt={history.date || ''}
                        height={60}
                        src={history.thumbnailUrl || ''}
                        width={80}
                        style={{
                          width: '80px',
                          height: '60px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '2px solid white',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* アラートポップアップ - コンポーネントの外に配置 */}
      <AlertPopup
        message='画面左上の「ホスト操作に追従」をオフにすると選択できます'
        visible={isAlertVisible}
        onClose={handleAlertClose}
      />
    </>
  );
}

/**
 * テキストシャドウのスタイルを取得する関数
 * @returns テキストシャドウのCSS値
 */
function getTextShadow(): string {
  return `
    -2px -2px 2px #4C4948,
    2px -2px 2px #4C4948,
    -2px 2px 2px #4C4948,
    2px 2px 2px #4C4948,
    0px 2px 2px #4C4948,
    2px 0px 2px #4C4948,
    0px -2px 2px #4C4948,
    -2px 0px 2px #4C4948
  `;
}
